import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/feature/ComicCard";
import JsonLd from "@/components/feature/JsonLd";
import { comics, categoryList } from "@/mocks/comics";

const siteUrl = "https://vhzhn02.cn";

function sortComics(list: typeof comics, key: "hot" | "new" | "done") {
  if (key === "hot") {
    return [...list].sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating));
  }
  if (key === "new") {
    return [...list].reverse();
  }
  return list.filter((c) => c.status === "已完结");
}

const faqs = [
  {
    q: "漫天堂的漫画库包含哪些内容？",
    a: "漫天堂漫画库聚合了热门连载、经典旧作与最新更新，覆盖热血动作、玄幻修真、恋爱日常、悬疑推理、搞笑日常、科幻末世等多个类型，并持续更新资源。",
  },
  {
    q: "如何快速找到我想看的漫画？",
    a: "你可以在站内检索框直接输入作品名称进行搜索，也可以通过顶部分类导航按类型浏览，配合热门连载与最新更新栏目快速定位目标作品。",
  },
  {
    q: "漫画的更新频率如何？",
    a: "热门连载作品会根据原作更新进度及时同步，最新更新栏目会实时展示近期的作品更新情况，方便你随时跟进。",
  },
  {
    q: "浏览需要付费吗？",
    a: "漫天堂的漫画目录浏览与检索完全免费，你可以自由浏览作品信息与章节目录，选择感兴趣的漫画开始阅读。",
  },
];

export default function Home() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");

  const hotComics = sortComics(comics, "hot").slice(0, 8);
  const latestComics = sortComics(comics, "new").slice(0, 4);
  const featuredComics = [comics[0], comics[4], comics[5], comics[7]];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/category/all?q=${encodeURIComponent(q)}` : "/category/all");
  };

  const webSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "漫天堂 - 漫画库与阅读索引",
    url: siteUrl,
    description:
      "漫天堂漫画库，聚合热门连载、经典旧作与最新更新，支持按分类浏览和站内检索，一站式发现你想看的漫画作品。",
    inLanguage: "zh-CN",
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "漫天堂",
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description: "漫画爱好者的阅读与索引平台。",
  };

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "漫天堂 - 漫画库与全站目录",
    url: siteUrl,
    isPartOf: { "@type": "WebSite", name: "漫天堂", url: siteUrl },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <JsonLd data={[webSchema, orgSchema, pageSchema, faqSchema]} />
      <Navbar />

      {/* Hero */}
      <section className="relative w-full overflow-hidden">
        <div className="relative flex min-h-[420px] w-full items-center justify-center md:min-h-[560px]">
          <img
            src="https://readdy.ai/api/search-image?query=stylized%20collage%20of%20floating%20anime%20manga%20books%20and%20comic%20pages%20in%20warm%20sunset%20tones%2C%20abstract%20artistic%20illustration%20with%20soft%20gradient%20glow%2C%20cinematic%20wide%20banner%2C%20rich%20texture%20and%20depth%2C%20clean%20modern%20background&width=1600&height=900&seq=hero-manga-library&orientation=landscape"
            alt="漫天堂漫画库"
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/60"></div>

          <div className="relative z-10 flex w-full max-w-3xl flex-col items-center gap-6 px-4 text-center md:gap-8">
            <span className="flex items-center gap-1.5 rounded-full bg-background-50/15 px-4 py-1.5 text-sm text-background-50 backdrop-blur">
              <i className="ri-fire-fill text-secondary-400" />
              热门连载 · 经典旧作 · 最新更新
            </span>
            <h1 className="font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
              漫天堂 <span className="text-secondary-300">漫画库</span>
              <br className="hidden md:block" /> 一站式发现你喜欢的漫画
            </h1>
            <p className="max-w-xl text-sm leading-relaxed text-background-100 md:text-base">
              聚合全站目录与资源汇总，支持按分类浏览与站内检索，热门连载、经典旧作、最新更新一网打尽，想看哪部直接搜。
            </p>

            {/* Search */}
            <form
              onSubmit={handleSearch}
              role="search"
              className="flex w-full max-w-lg items-center gap-2 rounded-full bg-background-50 px-2 py-1.5 shadow-lg"
            >
              <i className="ri-search-line pl-3 text-lg text-foreground-500" />
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索漫画名称或关键词"
                className="w-full bg-transparent px-2 py-1.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 md:text-base"
                aria-label="搜索漫画"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
              >
                立即搜索
              </button>
            </form>

            {/* quick category chips */}
            <div className="flex flex-wrap items-center justify-center gap-2">
              {categoryList
                .filter((c) => c.key !== "all")
                .slice(0, 5)
                .map((c) => (
                  <Link
                    key={c.key}
                    to={`/category/${c.key}`}
                    className="flex items-center gap-1.5 rounded-full bg-background-50/15 px-3 py-1.5 text-xs text-background-50 backdrop-blur hover:bg-primary-500"
                  >
                    <i className={c.icon} />
                    {c.name}
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </section>

      {/* Category quick nav */}
      <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6" id="categories">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            按分类浏览
          </h2>
          <Link
            to="/category/all"
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
          >
            全部漫画 <i className="ri-arrow-right-line" />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {categoryList
            .filter((c) => c.key !== "all")
            .map((c) => (
              <Link
                key={c.key}
                to={`/category/${c.key}`}
                className="group flex flex-col items-center gap-2 rounded-lg border border-background-200/70 bg-background-50 p-5 transition-colors hover:border-primary-300"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary-100 text-secondary-700 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className={`${c.icon} text-xl`} />
                </span>
                <span className="text-sm font-medium text-foreground-900">{c.name}</span>
                <span className="text-xs text-foreground-500">
                  {comics.filter((x) => x.category === c.key).length} 部作品
                </span>
              </Link>
            ))}
        </div>
      </section>

      {/* Hot serials */}
      <section className="mx-auto w-full max-w-7xl px-4 pb-10 md:px-6">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            热门连载
          </h2>
          <Link
            to="/category/all?sort=hot"
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
          >
            查看更多 <i className="ri-arrow-right-line" />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
          {hotComics.slice(0, 4).map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </section>

      {/* Latest updates */}
      <section className="bg-background-100/60">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              最新更新
            </h2>
            <Link
              to="/category/all?sort=new"
              className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
            >
              全部更新 <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
            {latestComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </div>
      </section>

      {/* Featured / hot grid */}
      <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            本周精选
          </h2>
          <span className="flex items-center gap-1 text-sm text-foreground-500">
            <i className="ri-flashlight-fill text-secondary-500" />
            编辑推荐
          </span>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-4">
          {featuredComics.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </section>

      {/* Value props */}
      <section className="bg-background-100/60">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6">
          <h2 className="mb-6 text-center font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            为什么选择漫天堂
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: "ri-book-3-line", title: "全站目录", desc: "漫画库资源汇总清晰，分类浏览一目了然。" },
              { icon: "ri-search-line", title: "站内检索", desc: "输入作品名即可快速定位，省去逐个翻找。" },
              { icon: "ri-refresh-line", title: "及时更新", desc: "热门连载与最新更新实时同步，紧跟进度。" },
              { icon: "ri-grid-line", title: "分类清晰", desc: "多类型索引整理，想看哪部直接进对应专区。" },
            ].map((f) => (
              <div
                key={f.title}
                className="rounded-lg border border-background-200/70 bg-background-50 p-5 text-center"
              >
                <span className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className={`${f.icon} text-xl`} />
                </span>
                <h3 className="font-heading text-base font-semibold text-foreground-950">{f.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto w-full max-w-4xl px-4 py-10 md:px-6">
        <h2 className="mb-6 text-center font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          常见问题
        </h2>
        <div className="flex flex-col gap-3">
          {faqs.map((f) => (
            <details
              key={f.q}
              className="group rounded-lg border border-background-200/70 bg-background-50 px-5 py-4"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-medium text-foreground-900 md:text-base">
                {f.q}
                <i className="ri-add-line text-foreground-500 transition-transform group-open:rotate-45" />
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <Footer />
    </>
  );
}